"use strict";
// @df/api - API service implementations
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserService = exports.ContactService = exports.NewsletterService = exports.PointCloudService = exports.InvitationService = exports.AuthService = void 0;
const client_1 = require("./client");
const apiBase_1 = require("./config/apiBase");
const auth_1 = require("./auth");
const helpers_1 = require("./helpers");
const SIGNUP_CODE_PATH = '/api/v1/invitations/signup-code/';
// Authentication service - Djoser + Custom Accounts alignment
class AuthService {
    // JWT Authentication (Djoser)
    // Login user - POST /auth/jwt/create/
    static async login(credentials) {
        // [AUTH-AUDIT] Log login call
        console.log('[AUTH-AUDIT] AuthService.login called', {
            hasAccessToken: !!auth_1.authManager.getAccessToken(),
            hasRefreshToken: !!auth_1.authManager.getRefreshToken()
        });
        const loginField = (0, apiBase_1.resolveJwtLoginField)();
        const payload = {
            [loginField]: credentials.username,
            password: credentials.password
        };
        return client_1.authClient.post('/auth/jwt/create/', payload, { isAuthRequest: true });
    }
    // Refresh token - POST /auth/jwt/refresh/
    static async refreshToken(refreshToken) {
        // [AUTH-AUDIT] Log refresh call
        console.log('[AUTH-AUDIT] AuthService.refreshToken called', {
            hasAccessToken: !!auth_1.authManager.getAccessToken(),
            hasRefreshToken: !!auth_1.authManager.getRefreshToken()
        });
        // JWT refresh is hosted on the Intermediate Server API origin, not the Auth Portal origin.
        return client_1.apiAuthClient.post('/auth/jwt/refresh/', { refresh: refreshToken }, { isAuthRequest: true });
    }
    // Verify JWT token - POST /auth/jwt/verify/
    static async verifyJwtToken(token) {
        // [AUTH-AUDIT] Log verify call
        console.log('[AUTH-AUDIT] AuthService.verifyJwtToken called', {
            hasAccessToken: !!auth_1.authManager.getAccessToken(),
            hasRefreshToken: !!auth_1.authManager.getRefreshToken()
        });
        // Mark as auth request to prevent refresh loop
        return client_1.authClient.post('/auth/jwt/verify/', { token }, { isAuthRequest: true });
    }
    // User Management (Djoser)
    // Register user - POST /auth/users/
    // This is a public endpoint that requires no authentication
    static async registerUser(userData) {
        return client_1.authClient.post('/auth/users/', userData, { isAuthRequest: true });
    }
    // Get current user - GET /api/v1/me/
    static async getMe() {
        return client_1.apiClient.get('/me/');
    }
    // Get current user (backward-compatible alias)
    static async getCurrentUser() {
        return AuthService.getMe();
    }
    // Password Reset (Djoser)
    // Request password reset - POST /auth/users/reset_password/
    // This is a public endpoint that requires no authentication
    static async requestPasswordReset(email) {
        return client_1.authClient.post('/auth/users/reset_password/', { email }, { isAuthRequest: true });
    }
    // Password reset confirm - POST /auth/users/reset_password_confirm/
    // This is a public endpoint that requires no authentication (uses uid/token for auth)
    static async passwordResetConfirm(uid, token, newPassword) {
        return client_1.authClient.post('/auth/users/reset_password_confirm/', {
            uid,
            token,
            new_password: newPassword
        }, { isAuthRequest: true });
    }
    // Email Activation (Djoser)
    // Verify email (activation) - POST /auth/users/activation/
    // This is a public endpoint that requires no authentication (uses uid/token for auth)
    static async verifyEmail(uid, token) {
        return client_1.authClient.post('/auth/users/activation/', { uid, token }, { isAuthRequest: true });
    }
    // Resend activation - POST /auth/users/resend_activation/
    // This is a public endpoint that requires no authentication
    static async resendVerification(email) {
        return client_1.authClient.post('/auth/users/resend_activation/', { email }, { isAuthRequest: true });
    }
    // Change password (authenticated) - POST /auth/users/set_password/
    static async changePassword(currentPassword, newPassword) {
        return client_1.authClient.post('/auth/users/set_password/', {
            current_password: currentPassword,
            new_password: newPassword
        });
    }
    // Custom Accounts Endpoints
    // Logout user - POST /accounts/logout/
    static async logout(refreshToken) {
        // Logout requires Bearer token in Authorization header + refresh token in body
        return client_1.apiClient.post('/accounts/logout/', { refresh: refreshToken });
    }
}
exports.AuthService = AuthService;
// Invitation codes service (invite-only signup)
class InvitationService {
    // Validate invitation code - GET /api/v1/invitations/codes/{code}/validate/
    static async validateInviteCode(code) {
        const encoded = encodeURIComponent(code);
        return client_1.authClient.get(`/api/v1/invitations/codes/${encoded}/validate/`);
    }
    // Is self-service signup open on the server? - GET /api/v1/invitations/signup-code/
    // Public endpoint: sent without an Authorization header.
    static async getSignupAvailability() {
        return client_1.authClient.request(SIGNUP_CODE_PATH, { method: 'GET' }, 0, true);
    }
    // Email a self-service sign-up code - POST /api/v1/invitations/signup-code/
    // No automatic retry after a 429: a retry would be a second code request the person didn't make.
    static async requestSignupCode(email) {
        return client_1.authClient.post(SIGNUP_CODE_PATH, { email }, { isAuthRequest: true, retryOnRateLimit: false });
    }
    // Validate scoped invitation token - POST /api/v1/invitations/scoped/validate/
    static async validateScopedInvitationToken(token) {
        return client_1.authClient.post('/api/v1/invitations/scoped/validate/', { token }, { isAuthRequest: true });
    }
    static async redeemScopedInvitationToken(token) {
        return client_1.apiClient.post('/invitations/scoped/redeem/', { token });
    }
    /**
     * Admin-only: redeem invitation code.
     * This is not intended for browser UI flows.
     */
    static async adminRedeemInvitationCode(code) {
        return client_1.authClient.post('/api/v1/invitations/codes/redeem', { code }, { isAuthRequest: true });
    }
}
exports.InvitationService = InvitationService;
// Blog service - ONBOARDING APP ONLY
// This service should only be used in the onboarding app for static blog content
// BlogService has been moved to apps/onboarding/src/services/blogService.js
// This isolates blog functionality to the Onboarding app only
// Filesystem service (updated to use filesystem endpoints)
class PointCloudService {
    // List files with pagination and search
    static async listFiles(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page)
            queryParams.append('page', params.page.toString());
        if (params.page_size)
            queryParams.append('page_size', params.page_size.toString());
        if (params.search)
            queryParams.append('search', params.search);
        if (params.folder_id)
            queryParams.append('folder_id', params.folder_id);
        if (params.project_id)
            queryParams.append('project_id', params.project_id);
        const endpoint = `/filesystem/files/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return client_1.apiClient.get(endpoint);
    }
    // Get file details by ID
    static async getFileDetails(fileId) {
        return client_1.apiClient.get(`/filesystem/files/${fileId}/`);
    }
    // Generate presigned download URL
    // Backend contract: GET /api/v1/filesystem/files/{id}/download_url/
    // Response: { url: string, expires_in?: number } or { presigned_url: string, expires_in: number }
    // This method normalizes the response to always return { url: string, expires_in?: number }
    static async getPresignedUrl(fileId) {
        const response = await client_1.apiClient.get(`/filesystem/files/${fileId}/download_url/`);
        // Normalize response: handle both 'url' and 'presigned_url' field names
        return {
            url: response.url || response.presigned_url || '',
            expires_in: response.expires_in
        };
    }
    // Download file from presigned URL and return as File object
    static async downloadFileFromUrl(url, filename) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Download failed: ${response.statusText}`);
        }
        const blob = await response.blob();
        const file = new File([blob], filename || 'download.las', {
            type: blob.type || 'application/octet-stream'
        });
        return file;
    }
    // Get file content directly from backend (for viewer ingestion)
    // Backend contract: GET /api/v1/filesystem/files/{id}/content/
    // Returns ArrayBuffer of file bytes
    static async getFileContent(fileId) {
        // Use fetch directly to get arraybuffer response
        const url = (0, apiBase_1.buildApiUrl)(`/filesystem/files/${fileId}/content/`);
        const accessToken = auth_1.authManager.getAccessToken();
        // Debug log (do not log full token for security)
        console.debug('[CONTENT][REQUEST]', {
            url,
            hasToken: !!accessToken,
            ts: new Date().toISOString()
        });
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                // NOTE: Do NOT send Accept header here - let browser use default (*/*)
                // Django REST Framework's content negotiation works with browser's default Accept
            },
        });
        if (!response.ok) {
            // Try to parse error response
            let errorMessage = `Failed to load file content: ${response.statusText}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || errorData.message || errorMessage;
            }
            catch {
                // Ignore JSON parse errors
            }
            throw new Error(errorMessage);
        }
        return await response.arrayBuffer();
    }
    // Delete file
    static async deleteFile(fileId) {
        return client_1.apiClient.delete(`/filesystem/files/${fileId}/`);
    }
    // Rename file
    static async renameFile(fileId, name) {
        const formData = new FormData();
        formData.append('name', name);
        // [RENAME][BUILD] Trace FormData construction
        console.debug('[RENAME][BUILD]', {
            fileId,
            nameLen: name.length,
            formDataKeys: Array.from(formData.keys()),
            ts: new Date().toISOString()
        });
        try {
            return await client_1.apiClient.patchForm(`/filesystem/files/${fileId}/`, formData);
        }
        catch (e) {
            // optional fallback if the API is mounted at /filesystem/{id}/ in some envs
            if (e?.status === 404 || e?.status === 405) {
                return await client_1.apiClient.patchForm(`/filesystem/${fileId}/`, formData);
            }
            throw e;
        }
    }
    // Move file
    static async moveFile(fileId, folderId, projectId) {
        return client_1.apiClient.patch(`/filesystem/files/${fileId}/move/`, {
            folder_id: folderId || null,
            project_id: projectId || null
        });
    }
    // Get storage usage/quota
    static async getStorageUsage(projectId) {
        const queryParams = new URLSearchParams();
        if (projectId)
            queryParams.append('project_id', projectId);
        const endpoint = `/filesystem/files/storage_usage/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return client_1.apiClient.get(endpoint);
    }
    // Folder operations
    static async listFolders(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page)
            queryParams.append('page', params.page.toString());
        if (params.page_size)
            queryParams.append('page_size', params.page_size.toString());
        if (params.parent)
            queryParams.append('parent', params.parent);
        if (params.project_id)
            queryParams.append('project_id', params.project_id);
        const endpoint = `/filesystem/folders/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return client_1.apiClient.get(endpoint);
    }
    static async createFolder(params) {
        return client_1.apiClient.post('/filesystem/folders/', {
            name: params.name,
            parent: params.parent || null,
            project: params.project || null
        });
    }
    static async deleteFolder(folderId) {
        return client_1.apiClient.delete(`/filesystem/folders/${folderId}/`);
    }
    static async getFolder(folderId) {
        return client_1.apiClient.get(`/filesystem/folders/${folderId}/`);
    }
    // Legacy methods for backward compatibility
    static getFiles(params = {}) {
        const endpoint = helpers_1.SearchHelper.addSearchToEndpoint('/filesystem/files/', params);
        return new helpers_1.PaginationHelper(endpoint);
    }
    static async getFile(fileId) {
        return this.getFileDetails(fileId);
    }
    static async downloadFile(fileId, filename) {
        const urlResponse = await this.getPresignedUrl(fileId);
        const file = await this.downloadFileFromUrl(urlResponse.url, filename);
        // Trigger browser download
        const link = document.createElement('a');
        link.href = urlResponse.url;
        link.download = filename || 'download.las';
        link.click();
    }
    static async getFileTree() {
        return this.listFolders();
    }
    static async getStorageUsageLegacy() {
        const result = await this.getStorageUsage();
        return { used: result.used, limit: result.limit };
    }
    // Upload file with progress reporting
    static async uploadFile(formData, options = {}) {
        const { onProgress, onSuccess, onError } = options;
        const formKeys = Array.from(formData.keys());
        const hasName = formData.has('name');
        let fileFromForm = null;
        let providedName = null;
        for (const key of formKeys) {
            const value = formData.get(key);
            if (value instanceof File) {
                fileFromForm = value;
                break;
            }
            if (key === 'name') {
                providedName = String(value || '').trim();
            }
        }
        if (hasName && !providedName) {
            formData.delete('name');
        }
        if (!hasName || !providedName) {
            const computedName = fileFromForm?.name?.trim();
            if (computedName) {
                formData.append('name', computedName);
            }
            else {
                const error = new Error('Missing file name for upload.');
                if (onError) {
                    onError(error);
                }
                throw error;
            }
        }
        // [CloudUpload] Step 3: One-shot OPTIONS probe (dev-only)
        if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && !window.__CLOUD_UPLOAD_OPTIONS_RUN) {
            window.__CLOUD_UPLOAD_OPTIONS_RUN = true;
            try {
                const endpoint = '/filesystem/files/';
                const url = (0, apiBase_1.buildApiUrl)(endpoint);
                const optionsResponse = await client_1.apiClient.request(endpoint, {
                    method: 'OPTIONS'
                }, 0, false); // isAuthRequest=false so Authorization header is attached
                console.log('[CloudUpload] OPTIONS', optionsResponse);
            }
            catch (e) {
                console.log('[CloudUpload] OPTIONS failed', {
                    status: e?.response?.status || e?.status,
                    statusText: e?.response?.statusText || e?.message,
                    error: e
                });
            }
        }
        // [CLOUD_UPLOAD_TRACE] Extract form data info before HTTP call
        let fileName;
        let fileSize;
        let projectId;
        let path;
        // Extract file info from FormData
        for (const key of formKeys) {
            const value = formData.get(key);
            if (value instanceof File) {
                fileName = value.name;
                fileSize = value.size;
            }
            else if (key === 'project_id' || key === 'projectId') {
                projectId = String(value);
            }
            else if (key === 'path' || key === 'folder' || key === 'folder_path') {
                path = String(value);
            }
        }
        // [CLOUD_UPLOAD_TRACE] Get token info for diagnostics
        const accessToken = auth_1.authManager.getAccessToken();
        const tokenSource = accessToken ? 'authManager' : 'none';
        const tokenPreview = accessToken ? accessToken.substring(0, 12) : null;
        // [CLOUD_UPLOAD_TRACE] Get URL info
        const endpoint = '/filesystem/files/';
        const url = (0, apiBase_1.buildApiUrl)(endpoint);
        // [CLOUD_UPLOAD_TRACE] Log structured request trace
        console.log(JSON.stringify({
            trace: 'CLOUD_UPLOAD_REQUEST',
            url,
            method: 'POST',
            hasAuthHeader: !!accessToken,
            contentType: 'multipart/form-data (browser-set)',
            formKeys,
            fileName,
            fileSize,
            projectId,
            path,
            tokenSource,
            tokenPreview
        }));
        try {
            // Simulate progress since fetch doesn't support it natively
            if (onProgress) {
                onProgress(10);
                setTimeout(() => onProgress(50), 100);
                setTimeout(() => onProgress(90), 200);
            }
            // [NO-RETRY] Temporarily disable retries for uploads to see clean failure
            // Use apiClient for authentication and error handling
            // Pass retryCount=999 to prevent 401 retry (temporary for diagnostics)
            const result = await client_1.apiClient.request(endpoint, {
                method: 'POST',
                body: formData
            }, 999, false); // retryCount=999 prevents 401 retry, isAuthRequest=false
            if (onProgress) {
                onProgress(100);
            }
            // Response logging is done in apiClient.request for upload requests
            // No need to log here to avoid duplication
            if (onSuccess) {
                onSuccess(result);
            }
            return result;
        }
        catch (error) {
            // Error logging is done in apiClient.request for upload requests
            // No need to log here to avoid duplication - apiClient already logs CLOUD_UPLOAD_ERROR
            // Map apiClient errors to the same error messages as before
            let errorMessage = 'Upload failed';
            if (error.status) {
                if (error.status === 400) {
                    errorMessage = 'Invalid file or validation error';
                }
                else if (error.status === 401) {
                    errorMessage = 'Authentication required';
                }
                else if (error.status === 413) {
                    errorMessage = 'File too large';
                }
                else if (error.status === 429) {
                    errorMessage = 'Rate limit exceeded';
                }
                else if (error.status >= 500) {
                    errorMessage = 'Server error';
                }
            }
            else if (error.message.includes('Network error')) {
                errorMessage = 'Network error during upload';
            }
            else if (error.message.includes('cancelled')) {
                errorMessage = 'Upload cancelled';
            }
            const uploadError = new Error(`${errorMessage}${error.status ? ` (${error.status})` : ''}`);
            uploadError.status = error.status;
            uploadError.data = error.data;
            if (onError) {
                onError(uploadError);
            }
            throw uploadError;
        }
    }
}
exports.PointCloudService = PointCloudService;
// Newsletter service
class NewsletterService {
    // Subscribe to newsletter
    static async subscribe(email, name) {
        return client_1.apiClient.post('/newsletter/subscribe/', { email, name });
    }
    // Unsubscribe from newsletter
    static async unsubscribe(email) {
        return client_1.apiClient.post('/newsletter/unsubscribe/', { email });
    }
}
exports.NewsletterService = NewsletterService;
// Contact service
class ContactService {
    // Submit contact form
    static async submitContactForm(form) {
        return client_1.apiClient.post('/contact/', form);
    }
}
exports.ContactService = ContactService;
// User service (for admin operations)
class UserService {
    // Get users (admin only)
    static getUsers(params = {}) {
        const endpoint = helpers_1.SearchHelper.addSearchToEndpoint('/users/', params);
        return new helpers_1.PaginationHelper(endpoint);
    }
    // Get single user
    static async getUser(userId) {
        return client_1.apiClient.get(`/users/${userId}/`);
    }
    // Update user
    static async updateUser(userId, userData) {
        return client_1.apiClient.put(`/users/${userId}/`, userData);
    }
    // Delete user
    static async deleteUser(userId) {
        return client_1.apiClient.delete(`/users/${userId}/`);
    }
}
exports.UserService = UserService;
// All services are already exported above with their class declarations
